// pushFoldChart.ts (TF1) - the browser-side loader for the DT push/fold Nash charts. The engine's own
// loader (tournament/testdata.loadPushFoldChart) reads the shipped JSON with node:fs, which cannot run in
// the browser; here we load the SAME committed charts through Vite's import.meta.glob - exactly the pattern
// tournamentData.ts uses for the payout structures. This is the piece PF5 needs live: the tournament path
// injects `ctx.pushFold = { chart: loadPushFoldChart(tableSize) }` so recommendPreflop routes a short-stack
// first-in open to the DT Nash jam-or-fold charts (docs/PF5_PREFLOP_RECONCILIATION.md §1). Vite inlines the
// JSON at build time, so the whole thing plays offline at $0.
import { PushFoldChartSchema, type PushFoldChart } from '@postflop/schema';

// Eager glob: each value is the parsed JSON (import: 'default'). Path is relative to THIS file
// (apps/web/src/engine) reaching up to the repo root, then into the engine's shipped push/fold data dir.
const chartModules = import.meta.glob(
  '../../../../packages/engine/src/data/tournament/pushfold/*.json',
  { eager: true, import: 'default' },
) as Record<string, unknown>;

/** All shipped push/fold charts, keyed by table size (6 or 9), each validated through the S2 zod schema. */
export const PUSH_FOLD_CHARTS: Map<6 | 9, PushFoldChart> = (() => {
  const map = new Map<6 | 9, PushFoldChart>();
  for (const raw of Object.values(chartModules)) {
    const chart = PushFoldChartSchema.parse(raw);
    if (chart.tableSize === 6 || chart.tableSize === 9) map.set(chart.tableSize, chart);
  }
  return map;
})();

/** The DT Nash push/fold chart for a table size (throws if absent - a corpus/logic bug, never a user
 *  path). This is the browser-safe sibling of the engine's node-only `loadPushFoldChart`. */
export const loadPushFoldChart = (tableSize: 6 | 9): PushFoldChart => {
  const chart = PUSH_FOLD_CHARTS.get(tableSize);
  if (!chart) throw new Error(`pushFoldChart: no push/fold chart for ${tableSize}-max in corpus`);
  return chart;
};
